import { useTranslation } from "react-i18next";
import { Badge } from "@nexus/ui";
import type { ScheduledJobHealth } from "@/features/scheduled-jobs/api/scheduled-jobs.api";

interface ScheduledJobHealthBadgeProps {
  health: ScheduledJobHealth | null;
  /** Enabled job whose reported next run is already in the past. */
  isOverdue: boolean;
  /** Streak length at which the badge turns red rather than amber. From the ops settings. */
  unhealthyAfterFailures?: number;
}

/** Below this streak a failure reads as a warning, not an outage. */
const DEFAULT_UNHEALTHY_AFTER_FAILURES = 1;

/**
 * One badge, ranked by how urgent it is to notice: a live failure streak
 * outranks "overdue" (which might just mean the job has not fired yet), which
 * outranks "no run history at all" (an HTTP-target job, or one that decoded
 * to no known event — nothing here to correlate against `event_jobs`).
 * A streak shorter than the unhealthy threshold shows amber rather than red,
 * so one flaky run does not read as an outage.
 */
export function ScheduledJobHealthBadge({
  health,
  isOverdue,
  unhealthyAfterFailures = DEFAULT_UNHEALTHY_AFTER_FAILURES,
}: ScheduledJobHealthBadgeProps) {
  const { t } = useTranslation();

  if (health && health.consecutiveFailures > 0) {
    const isUnhealthy = health.consecutiveFailures >= unhealthyAfterFailures;
    return (
      <Badge variant={isUnhealthy ? "status-error" : "status-in-production"} dot>
        {t("admin.scheduledJobs.health.failing", "{{count}} failing", {
          count: health.consecutiveFailures,
        })}
      </Badge>
    );
  }

  if (isOverdue) {
    return (
      <Badge variant="status-in-production" dot>
        {t("admin.scheduledJobs.health.overdue", "Overdue")}
      </Badge>
    );
  }

  if (!health) {
    return (
      <Badge variant="slate">{t("admin.scheduledJobs.health.untracked", "Not tracked")}</Badge>
    );
  }

  return (
    <Badge variant="status-delivered" dot>
      {t("admin.scheduledJobs.health.healthy", "Healthy")}
    </Badge>
  );
}
